import { useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle2, XCircle, Loader2, ArrowLeft, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLLS = 8;

const Shell = ({ children }) => (
  <div className="min-h-screen flex flex-col items-center justify-center bg-[var(--bg-primary)] px-6 text-center">
    {children}
  </div>
);

export const PaymentSuccess = () => {
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [info, setInfo] = useState(null);

  useEffect(() => {
    const sessionId = new URLSearchParams(window.location.search).get("session_id");
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setInfo({ ...data, sessionId });
          return setState("paid");
        }
        if (["failed", "expired"].includes(data.payment_status)) return setState("error");
      } catch {
        return setState("error");
      }
      attempts += 1;
      if (attempts >= MAX_POLLS) return setState("pending");
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, []);

  const isEbook = info?.type === "product" && info?.lookup_key === "ebook_placeholder";

  const home = (
    <a href="/" data-testid="back-home" className="mt-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors">
      <ArrowLeft size={16} /> Back to home
    </a>
  );

  return (
    <Shell>
      <div data-testid="payment-success-page" className="max-w-lg">
        {state === "checking" && (
          <>
            <Loader2 size={48} className="text-[var(--accent)] animate-spin mx-auto" />
            <h1 className="font-serif text-3xl md:text-4xl text-[var(--text-primary)] mt-8">Confirming your payment…</h1>
            <p className="font-sans text-[var(--text-secondary)] mt-3">One moment while we verify with Stripe.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 size={56} className="text-[var(--accent)] mx-auto" />
            <h1 className="font-serif text-4xl md:text-5xl text-[var(--text-primary)] mt-8">Payment received.</h1>
            {isEbook ? (
              <>
                <p className="font-sans text-[var(--text-secondary)] mt-3">Thank you for buying <span className="text-[var(--text-primary)]">Accounting Made Simple</span>. Your download is ready — a receipt has also been emailed to you by Stripe.</p>
                <a
                  href={`${API}/downloads/ebook/${info.sessionId}`}
                  data-testid="download-ebook"
                  className="mt-8 inline-flex items-center gap-3 px-8 py-4 rounded-full bg-[var(--accent)] text-[var(--bg-primary)] font-semibold hover:bg-[var(--accent-hover)] transition-colors duration-300"
                >
                  <Download size={18} /> Download your ebook (PDF)
                </a>
                <p className="font-sans text-xs text-[var(--text-secondary)] mt-4">Keep this page — you can re-download from this link anytime.</p>
              </>
            ) : (
              <p className="font-sans text-[var(--text-secondary)] mt-3">Thank you — a receipt has been emailed to you by Stripe. We'll be in touch with next steps.</p>
            )}
            {home}
          </>
        )}
        {state === "pending" && (
          <>
            <Loader2 size={48} className="text-[var(--accent)] mx-auto" />
            <h1 className="font-serif text-3xl md:text-4xl text-[var(--text-primary)] mt-8">Almost there.</h1>
            <p className="font-sans text-[var(--text-secondary)] mt-3">Your payment is still processing. It may take a moment to confirm — check your email shortly.</p>
            {home}
          </>
        )}
        {state === "error" && (
          <>
            <XCircle size={56} className="text-[var(--text-secondary)] mx-auto" />
            <h1 className="font-serif text-4xl md:text-5xl text-[var(--text-primary)] mt-8">We couldn't confirm this.</h1>
            <p className="font-sans text-[var(--text-secondary)] mt-3">If you were charged, don't worry — reach out and we'll sort it out right away.</p>
            {home}
          </>
        )}
      </div>
    </Shell>
  );
};

export const PaymentCancel = () => (
  <Shell>
    <div data-testid="payment-cancel-page" className="max-w-lg">
      <XCircle size={56} className="text-[var(--text-secondary)] mx-auto" />
      <h1 className="font-serif text-4xl md:text-5xl text-[var(--text-primary)] mt-8">Payment cancelled.</h1>
      <p className="font-sans text-[var(--text-secondary)] mt-3">No charge was made. You can head back and try again whenever you're ready.</p>
      <a href="/" data-testid="cancel-back-home" className="mt-10 inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[var(--accent)] text-[var(--bg-primary)] font-semibold hover:bg-[var(--accent-hover)] transition-colors duration-300">
        <ArrowLeft size={18} /> Back to home
      </a>
    </div>
  </Shell>
);
